/**
 * Error copy for the admin app's Settings → Outreach pages (mailboxes, suppressions, erasure, diagnostics and
 * attaching an unmatched reply), which used to show Go's raw sentences (owner 2026-10-12).
 */
import { apiErrorCopy, codeOf, detailsOf, statusOf } from './sendLabels';

const REFRESH_LIST = 'Refresh the list and try again.';

const ADMIN_ERROR_COPY: Record<string, string> = {
  MAILBOX_EXISTS: 'This mailbox is already in the pool.',
  DOMAIN_NOT_ALLOWED: "Discovr can't send from that domain in this environment. Use an address on one of the allowed sending domains.",
  INVALID_ADDRESS: "That isn't a valid email address.",
  INVALID_DISPLAY_NAME: 'Display name must be 1 to 120 characters.',
  DELEGATION_NOT_GRANTED: "Google Workspace hasn't given Discovr access to this address yet. Grant it in the Workspace admin console, then try again.",
  GMAIL_VERIFY_FAILED: "We couldn't reach Gmail to check this mailbox. Try again in a moment.",
  GMAIL_UNAVAILABLE: "Google access isn't set up in this environment, so mailboxes can't be checked or used.",
  DOMAIN_LIMIT_REACHED: 'This sending domain already has the most mailboxes it can have. Disable one before adding another.',
  INVALID_DAILY_CAP: "That daily cap isn't allowed. Pick a number from 1 up to the mailbox's warm-up limit.",
  CAP_ABOVE_WARMUP: "That's above this mailbox's warm-up limit. Pick a lower daily cap.",
  MAILBOX_NOT_FOUND: 'This mailbox no longer exists. Refresh the page.',
  INVALID_TRANSITION: "This mailbox's status changed, so that can't be done now. Refresh the page and try again.",
  MAILBOX_ALREADY_RESYNCING: 'A full sync is already running for this mailbox.',
  MAILBOX_IN_USE: "Messages are still queued on this mailbox, so it can't be disabled. Pause it instead.",
  INVALID_FROM: 'A test email can only come from a pool mailbox.',
  ENQUEUE_FAILED: "The test email couldn't be queued. Try again.",
  INVALID_SCOPE: 'Pick a scope for the suppression: Global, Brand or Campaign.',
  INVALID_SCOPE_TARGET: "The brand or campaign doesn't match the scope you picked. Pick it again.",
  INVALID_SCOPE_BRAND_ID: "That brand can't be found. Pick it again.",
  INVALID_SCOPE_CAMPAIGN_ID: "That campaign can't be found. Pick it again.",
  INVALID_CAMPAIGN_ID: "That campaign can't be found. Pick it again.",
  INVALID_REASON: 'Only a manual suppression can be added here.',
  SUPPRESSION_SYSTEM_REASON: 'This suppression was added automatically (a bounce, complaint or unsubscribe), so it stays.',
  SUPPRESSION_NOT_FOUND: `This suppression was already removed. ${REFRESH_LIST}`,
  INVALID_SUPPRESSION_ID: `This suppression can't be found. ${REFRESH_LIST}`,
  INVALID_USER_ID: "That isn't a valid creator user id. Check it and try again.",
  INVALID_JOB_ID: `This job can't be found. ${REFRESH_LIST}`,
  QUARANTINE_UNAVAILABLE: "We couldn't read the stuck jobs right now. Try again in a moment.",
  REPLAY_FAILED: "The job couldn't be queued again. Try again in a moment.",
  MESSAGE_ALREADY_ATTACHED: `This message is already attached to a creator. ${REFRESH_LIST}`,
  MESSAGE_NOT_INBOUND: 'Only a message a creator sent us can be attached.',
  MESSAGE_NOT_FOUND: `This message is gone. ${REFRESH_LIST}`,
  INVALID_US_POSTAL_ADDRESS: 'The US postal address must be plain text on one line, up to 300 characters, with no blanks to fill in.',
  ENTRY_NOT_FOUND: "This creator has no outreach in that campaign. Pick another creator.",
};

/** The admin page's copy for a server error: its own words for the admin routes' codes, then the outreach copy
 *  (network, session, generic codes), then Go's sentence, then the caller's line. */
export function adminOutreachErrorCopy(error: unknown, fallback: string): string {
  const code = codeOf(error);
  const own = code && Object.hasOwn(ADMIN_ERROR_COPY, code) ? ADMIN_ERROR_COPY[code] : undefined;
  if (own) return own;
  if (code === 'INVALID_SIGNATURE') return signatureCopy(error);
  if (statusOf(error) === 403) return "You don't have permission to do that.";
  return apiErrorCopy(error, fallback);
}

// Go's reason names the problem ("the signature is too long"); a reason this build doesn't know reads generically.
function signatureCopy(error: unknown): string {
  const reason = detailsOf(error)?.reason ?? (error instanceof Error ? error.message : '');
  return typeof reason === 'string' && /too long/i.test(reason)
    ? 'The signature is too long. Shorten it and try again.'
    : "The signature couldn't be saved. Check it and try again.";
}
